import React from 'react';

import { useTheme } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { updateOKR } from 'components/RabbitOKRCreationPanel/OKRCreationPanelSlice';
import { QUERY_KEYS } from 'components/RabbitOKRSidePanel/datahooks';
import { useOKRRealignMutation } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignMutation';
import { useOKRRealignViewData } from 'components/RabbitOKRSidePanel/datahooks/useOKRRealignViewData';
import { closeRealignPanel, openTitleAndMetricPanel } from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useOKRDrawerControlCallbacks } from 'components/RabbitOKRSidePanel/useOKRDrawerControlCallbacks';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { okrCreationPanelStateSelector } from 'selectors/okrCreationPanelSelector';

import { OBJECTIVE_LEVEL, OKR_TYPES, OKR_PROGRESS_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { modifyString, StringModifier } from 'constants/copyHelpers';
import { getTimePeriodIdString } from 'constants/helper';
import { sendRealignedKREvent, sendRealignedObjectiveEvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';

export const RealignFooterV2 = ({
  localSelectedTimePeriod,
  setLocalSelectedTimePeriod,
  selectedParent,
  setSelectedParent,
}) => {
  const theme = useTheme();
  const toast = useToast();
  const page = useViewPageName();
  const queryClient = useQueryClient();

  const { details } = useSelector(kRDrawerSecondaryPanelSelector);

  const { onUpdate } = useOKRDrawerControlCallbacks();

  const dispatch = useDispatch();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { okrTimePeriod, okrUuid, okrType, objectiveLevel, parentUuid, parentText, parentType, parentVisibilityType } =
    details;

  /* 
    read primary kr panel details, this will work but
    so far this component was relying only on secondaryPanel state
  */
  const { timeBoundDetails: okrSidePanelTimeBoundDetails } = useSelector(kRDrawerSelector);

  /* 
    read okr creation details, this will work but 
    so far this component was relying only on secondaryPanel state 
  */
  const { timeBoundDetails: okrCreationPanelTimeBoundDetails } = useSelector(okrCreationPanelStateSelector);

  const isCreateNewOKRWorkflow = okrUuid === null;

  const timeBoundDetails = isCreateNewOKRWorkflow ? okrCreationPanelTimeBoundDetails : okrSidePanelTimeBoundDetails;

  const isSelectedTimelineSameAsCurrentOKRTimeline =
    getTimePeriodIdString(timeBoundDetails) === getTimePeriodIdString(localSelectedTimePeriod);

  /* 
    Following code is to handle remove alignment based on privileges
    Starts here
  */
  const { canCreateKeyResultAtRootLevel } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const cannotRealignKeyResultAtRoot = okrType !== OKR_TYPES.OBJECTIVE && !canCreateKeyResultAtRootLevel;

  const cannotRealignObjectiveAtRoot = okrType === OKR_TYPES.OBJECTIVE && objectiveLevel !== OBJECTIVE_LEVEL.COMPANY;

  const hideRealignAsRoot =
    !parentUuid ||
    cannotRealignKeyResultAtRoot ||
    cannotRealignObjectiveAtRoot ||
    !isSelectedTimelineSameAsCurrentOKRTimeline;

  const canRealignThisOKRAtRoot =
    okrType === OKR_TYPES.OBJECTIVE ? objectiveLevel === OBJECTIVE_LEVEL.COMPANY : canCreateKeyResultAtRootLevel;

  const showRealignInThisTimelineAsRoot = !isSelectedTimelineSameAsCurrentOKRTimeline && canRealignThisOKRAtRoot;

  const onCancel = () => {
    if (details.isOpenAsChild) {
      const { suggestedOKRDetails } = details.parentState.details.okrDetails;
      const { currentCardIndex } = details.parentState.details.okrDetails;
      dispatch(
        openTitleAndMetricPanel({
          ...details.parentState.details,
          okrDetails: {
            ...details.parentState.details.okrDetails,
            suggestedOKRDetails,
            currentCardIndex,
          },
        })
      );
    } else {
      // @ts-ignore
      dispatch(closeRealignPanel());
    }
  };

  const { mutate, isLoading: isMutating } = useOKRRealignMutation(okrUuid);

  const { data, isLoading } = useOKRRealignViewData({
    timeBoundPeriod: localSelectedTimePeriod,
  });

  const onSelectParentOKR = ({ selectedParent: curSelectedParent, selectedTimePeriod }) => {
    let payload = {
      parentPost: null,
      timeBoundDetails: selectedTimePeriod,
      parentProgressType: null,
      parentMeasure: null,
      parentMeasureType: null,
      startTS: selectedTimePeriod?.startTS,
      dueTS: selectedTimePeriod?.endTS,
    };

    const isRemoveParentWorkflow = curSelectedParent === null;

    if (!isRemoveParentWorkflow) {
      const {
        uuid,
        text,
        type,
        visibilityType,
        progressType: parentProgressType,
        measure: parentMeasure,
        measureType: parentMeasureType,
      } = curSelectedParent;

      payload = {
        ...payload,
        parentPost: {
          uuid,
          text,
          type,
          visibilityType,
        },
        parentProgressType,
        parentMeasureType,
        parentMeasure,
      };

      /* for derived sum parent progress type set progress Type as not independent (if not already ) */
      if (parentProgressType === OKR_PROGRESS_TYPES.DERIVED_SUM) {
        // @ts-ignore
        payload.progressType = OKR_PROGRESS_TYPES.MANUAL;
        // @ts-ignore

        payload.measureType = parentMeasureType;

        // @ts-ignore
        payload.measure = parentMeasure;
      }
    }

    if (okrType === OKR_TYPES.OBJECTIVE) {
      sendRealignedObjectiveEvent({
        eventProperties: {
          goalId: okrUuid,
          type: details?.eventType || 'Realign',
          source: details?.source || 'Kebab Menu',
          page,
        },
      });
    } else {
      sendRealignedKREvent({
        eventProperties: {
          goalId: okrUuid,
          type: details?.eventType || 'Realign',
          source: details?.source || 'Kebab Menu',
          page,
        },
      });
    }

    if (details.isOpenAsChild) {
      const { suggestedOKRDetails } = details.parentState.details.okrDetails;
      const { currentCardIndex } = details.parentState.details.okrDetails;
      const newSuggestedOKRDetails = cloneDeep(suggestedOKRDetails);
      newSuggestedOKRDetails[currentCardIndex].parentPost = payload.parentPost;
      newSuggestedOKRDetails[currentCardIndex].timeBoundDetails = selectedTimePeriod;
      dispatch(
        openTitleAndMetricPanel({
          ...details.parentState.details,
          okrDetails: {
            ...details.parentState.details.okrDetails,
            suggestedOKRDetails: newSuggestedOKRDetails,
            currentCardIndex,
          },
        })
      );
    } else {
      dispatch(updateOKR(payload));
      // @ts-ignore
      dispatch(closeRealignPanel());
    }
  };

  const onSave = async ({ selectedParentUuid, selectedTimePeriod }) => {
    /* Queries to invalidate
      1.1 OKR ( so that parent and timeline component reflects updated )
      1.2 OKR so that progress updates can reflect ( in case of derived progress)
      2. refetch alignment section.
    */
    const okrQueryKey = [QUERY_KEYS.KEY_RESULT, okrUuid];
    const alignmentQueryKey = [QUERY_KEYS.OKR_TOP_HIERARCHY, okrUuid];
    try {
      mutate(
        // @ts-ignore
        {
          selectedParentUuid,
          timeBoundPeriod: selectedTimePeriod,
          performChildrenRealign: false,
        },
        {
          onError: (err) => {
            toast({
              // @ts-ignore
              title: err?.message || 'Failed to realign.',
              status: TOAST_STATUSES.ERROR,
            });
          },
          onSuccess: () => {
            queryClient.invalidateQueries(okrQueryKey);
            queryClient.invalidateQueries(alignmentQueryKey);

            if (okrType === OKR_TYPES.OBJECTIVE) {
              sendRealignedObjectiveEvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType || 'Realign',
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            } else {
              sendRealignedKREvent({
                eventProperties: {
                  goalId: okrUuid,
                  type: details?.eventType || 'Realign',
                  source: details?.source || 'Kebab Menu',
                  page,
                },
              });
            }

            if (typeof onUpdate === 'function') {
              onUpdate();
            }

            toast({
              title: `The ${
                okrType === OKR_TYPES.OBJECTIVE
                  ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
                  : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)
              } has been successfully re-aligned`,
              status: TOAST_STATUSES.SUCCESS,
              position: 'bottom-left',
            });
            // @ts-ignore
            dispatch(closeRealignPanel());
          },
        }
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  const onRealign = () => {
    if (isCreateNewOKRWorkflow) {
      onSelectParentOKR({
        selectedParent,
        selectedTimePeriod: localSelectedTimePeriod,
      });
    } else {
      onSave({
        selectedParentUuid: selectedParent?.uuid,
        selectedTimePeriod: localSelectedTimePeriod,
      });
    }
  };

  const onRemoveAlignment = () => {
    if (isCreateNewOKRWorkflow) {
      onSelectParentOKR({
        selectedParent: null,
        selectedTimePeriod: localSelectedTimePeriod,
      });
    } else {
      onSave({
        selectedParentUuid: null,
        selectedTimePeriod: localSelectedTimePeriod,
      });
    }
  };

  const handleRealignInThisTimelineAsRoot = onRemoveAlignment;

  const isRemovingAlignment = false;

  const disableRealign = isLoading || selectedParent?.uuid === parentUuid;

  return (
    <>
      <Button variant="tertiary" mr="auto" onClick={onCancel}>
        Cancel
      </Button>
      {hideRealignAsRoot ? null : (
        <Button variant="outlined" mr={4} onClick={onRemoveAlignment} isLoading={isRemovingAlignment}>
          Remove Alignment
        </Button>
      )}
      {showRealignInThisTimelineAsRoot ? (
        <Button variant="outlined" mr={4} onClick={handleRealignInThisTimelineAsRoot} isLoading={isRemovingAlignment}>
          Align at Strategic Level
        </Button>
      ) : null}

      <Button
        variant="filled"
        colorScheme="blue"
        onClick={onRealign}
        disabled={disableRealign}
        isLoading={!isRemovingAlignment && isMutating}
      >
        Realign
      </Button>
    </>
  );
};
